import React, { useState, useEffect, useRef } from 'react';
import { colors, FONT } from '../../theme';
import { imageCache } from '../../services/imageCache';
import { messageStore } from '../../services/messageStore.mjs';
import { fetchFileObjectURL } from '../../services/fileFetch';

export default function ImageMessage({ fileId, fileKey, fileMime, fileName, isMine, onOpen }) {
  const [src, setSrc] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const urlRef = useRef(null);
  const fromCacheRef = useRef(false);

  useEffect(() => {
    const cached = imageCache.get(fileId);
    if (cached) {
      urlRef.current = cached;
      fromCacheRef.current = true;
      setSrc(cached);
      return;
    }

    fetchFileObjectURL(fileId, fileKey, fileMime)
      .then(url => {
        urlRef.current = url;
        fromCacheRef.current = false;
        setSrc(url);
        imageCache.set(fileId, url);
      })
      .catch(async (err) => {
        // The server's copy is not forever, and after a phone change it is
        // unreadable anyway — which is why the history archive carries the
        // bytes. If this device has restored one, the picture is right here.
        try {
          const b64 = await messageStore.getAttachment(String(fileId));
          if (b64) {
            const bin = atob(b64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const url = URL.createObjectURL(new Blob([bytes]));
            urlRef.current = url;
            fromCacheRef.current = false;
            setSrc(url);
            imageCache.set(fileId, url);
            console.log('[ImageMessage] served', fileId, 'from the restored archive');
            return;
          }
        } catch (e) { /* fall through to the honest error below */ }

        // Saying nothing leaves "Загрузка…" on screen forever. A picture whose
        // file is gone — deleted, expired, or never uploaded — is a fact, and
        // an endless spinner is a lie about it.
        console.warn('[ImageMessage] load failed:', err.message);
        setLoadError(/404/.test(err.message) ? 'Файл удалён' : 'Не удалось загрузить');
      });

    return () => {
      if (urlRef.current && !imageCache.has(fileId)) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [fileId]);

  return (
    <div
      onClick={() => src && onOpen?.(fileId, fileName)}
      style={{
        cursor: src ? 'pointer' : 'default',
        borderRadius: 12,
        overflow: 'hidden',
        maxWidth: 240,
        background: isMine ? 'rgba(110,138,208,0.12)' : colors.gray100,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={fileName || 'Фото'}
          style={{ display: 'block', width: '100%', maxHeight: 300, objectFit: 'cover' }}
        />
      ) : (
        <div style={{
          width: 200, height: 140,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: loadError ? colors.gray500 : colors.gray400, fontSize: 13, fontFamily: FONT,
        }}>
          {loadError || 'Загрузка…'}
        </div>
      )}
    </div>
  );
}
